
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, GripVertical, X, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { TutorialStep } from "@/types/tutorial";
import RichTextEditor from "./RichTextEditor";

interface CollapsibleStepProps {
  step: TutorialStep;
  stepIndex: number;
  onUpdate: (updates: Partial<TutorialStep>) => void;
  onRemove: () => void;
  onAddImage: (imageUrl: string) => void;
  onRemoveImage: (imageIndex: number) => void;
  allExpanded?: boolean;
}

const CollapsibleStep: React.FC<CollapsibleStepProps> = ({
  step,
  stepIndex,
  onUpdate,
  onRemove,
  onAddImage,
  onRemoveImage,
  allExpanded = true
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    setIsExpanded(allExpanded);
  }, [allExpanded]);

  const getStepSummary = () => {
    // Strip HTML tags for summary
    const textContent = step.text.replace(/<[^>]*>/g, '');
    if (textContent && textContent.length > 50) {
      return textContent.substring(0, 50) + "...";
    }
    return textContent || "No instructions";
  };

  const hasImages = step.images && step.images.length > 0;

  return (
    <div 
      className="bg-gradient-to-r from-muted/30 to-muted/10 rounded-lg p-4 space-y-3 border border-border/50"
      data-step-collapsible
      data-step-id={step.id}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={16} className="text-muted-foreground cursor-grab mt-2" />
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-0 h-auto"
                data-collapse-trigger
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
              <Label className="text-sm font-medium bg-background/80 px-2 py-1 rounded">
                Step {stepIndex + 1}
              </Label>
              {hasImages && !isExpanded && (
                <span className="text-xs text-muted-foreground bg-background/60 px-2 py-1 rounded">
                  ({step.images.length} image{step.images.length > 1 ? 's' : ''})
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-destructive hover:text-destructive h-6 w-6 p-0"
            >
              <Trash2 size={12} />
            </Button>
          </div>

          {!isExpanded && (
            <div className="text-sm text-muted-foreground pl-6 bg-background/40 p-2 rounded">
              {getStepSummary()}
            </div>
          )}

          {isExpanded && (
            <div className="space-y-3 bg-background/40 p-3 rounded">
              <div className="space-y-2">
                <Label className="text-sm">Step Instructions</Label>
                <RichTextEditor
                  content={step.text}
                  onChange={(content) => onUpdate({ text: content })}
                  placeholder="Enter step instructions..."
                  className="min-h-[150px]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Images</Label>
                {step.images.map((image, imageIndex) => (
                  <div key={imageIndex} className="flex items-center gap-2">
                    <Input value={image} readOnly className="text-xs bg-background" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveImage(imageIndex)}
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    placeholder="Image URL"
                    className="text-xs bg-background"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        onAddImage(e.currentTarget.value);
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                      onAddImage(input.value);
                      input.value = "";
                    }}
                  >
                    <Plus size={12} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleStep;
